import { useCallback } from 'react';
import { Link as RouterLink, generatePath } from 'quartz-routing/react-router';
import { useTicketDetailsParams } from '../hooks/useTicketDetailsParams';
import { Flex } from 'foundations-components/components/Flex';
import styled from 'foundations-theming-specialty/utils';
import { ROUTES } from '../constants/routes';
import { usePortalTranslations } from 'portals-platform-lib';
import I18n from 'I18n';
import { useOnDelayedHover } from '../utils/useOnDelayedHover';
import { usePrefetchTicketData } from '../hooks/usePrefetchTicketData';
import { usageTracker } from '../utils/usageTracker';
import { InboxNavTicketRowHeader } from './InboxNavTicketRowHeader';
import { InboxNavTicketRowContent } from './InboxNavTicketRowContent';
import { formatTicketId } from '../utils/formatTicketId';
import { useConfiguredColumnLabelOverride } from '../utils/useConfiguredColumnLabelOverride';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const RowLink = styled(RouterLink).withConfig({
  displayName: "InboxNavTicketRow__RowLink"
})(["display:block;padding-block:", ";padding-inline:", ";border-block-end:", " solid ", ";text-decoration:none;color:inherit;&:hover{background-color:", ";text-decoration:none;}&[data-selected='true'],&[data-selected='true']:hover{background-color:", ";}"], ({
  theme
}) => theme.space['300'], ({
  theme
}) => theme.space['600'], ({
  theme
}) => theme.borderWidth[100], ({
  theme
}) => theme.color['border-core-subtle'], ({
  theme
}) => theme.color['fill-surface-recessed'], ({
  theme
}) => theme.color['fill-surface-recessed']);
export const InboxNavTicketRow = ({
  ticket,
  onClick
}) => {
  const {
    ticketId: currentTicketId
  } = useTicketDetailsParams();
  const prefetchTicketData = usePrefetchTicketData();
  const {
    portalText
  } = usePortalTranslations();
  const requestedByLabelOverride = useConfiguredColumnLabelOverride('__requested_by_full_name_or_email');
  const requestedByDisplayName = portalText('SUPPORT_SPACE_LISTINGS_PAGE_TICKET_PROPERTIES', '__requested_by_full_name_or_email') || requestedByLabelOverride;
  const showOpenDot = ticket.status === 'OPEN';
  const showClosedDot = ticket.status === 'CLOSED';
  const ticketId = ticket.hs_ticket_id;
  const isSelected = currentTicketId === ticketId;
  const title = typeof ticket.subject === 'string' && ticket.subject.length > 0 ? ticket.subject : I18n.text('customer-portal-app.inboxNavPanel.ticketRow.fallbackTitle', {
    ticketId: formatTicketId(ticketId)
  });
  const timestamp = typeof ticket.createdate === 'number' ? ticket.createdate : null;
  const content = typeof ticket.content === 'string' && ticket.content.length > 0 ? ticket.content : null;
  const requestedBy = typeof ticket.__requested_by_full_name_or_email === 'string' && ticket.__requested_by_full_name_or_email.length > 0 ? ticket.__requested_by_full_name_or_email : null;
  const handleClick = useCallback(() => {
    usageTracker.track('Customer Portal App', 'Tickets Discovered', {
      action: 'ticket-expanded',
      screen: 'ticket-details',
      initiator: 'inbox-nav-row-click'
    });
    onClick === null || onClick === void 0 || onClick();
  }, [onClick]);
  const onHover = useCallback(id => prefetchTicketData(Number(id)), [prefetchTicketData]);
  const hoverProps = useOnDelayedHover(onHover);
  return /*#__PURE__*/_jsx("div", {
    role: "listitem",
    children: /*#__PURE__*/_jsx(RowLink, Object.assign({}, hoverProps, {
      id: String(ticketId),
      to: generatePath(ROUTES.ticket, {
        ticketId
      }),
      onClick: handleClick,
      "data-test-id": "inbox-nav-ticket-row",
      "data-selected": isSelected,
      "aria-label": title,
      children: /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        gap: "100",
        children: [/*#__PURE__*/_jsx(InboxNavTicketRowHeader, {
          title: title,
          timestamp: timestamp,
          showOpenDot: showOpenDot,
          showClosedDot: showClosedDot
        }), /*#__PURE__*/_jsx(InboxNavTicketRowContent, {
          content: content,
          requestedBy: requestedBy,
          requestedByDisplayName: requestedByDisplayName
        })]
      })
    }))
  });
};